import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axiosInstance from '../../lib/axios';
import { 
  Store, Package, ShoppingCart, IndianRupee, TrendingUp, 
  Users, BarChart3, Eye, RefreshCw
} from 'lucide-react';
import { formatPrice } from '../../lib/utils';

export default function AdminVendorAnalytics() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [vendorStats, setVendorStats] = useState({});
  const [vendorOrders, setVendorOrders] = useState([]);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchVendors();
    fetchAllVendorStats();
  }, []);

  useEffect(() => {
    if (selectedVendor && selectedVendor !== 'all') {
      fetchVendorDetails(selectedVendor);
    }
  }, [selectedVendor]);

  const fetchVendors = async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/vendors`);
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchAllVendorStats = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${API_URL}/api/admin/vendor-analytics`);
      setVendorStats(response.data || {});
    } catch (error) {
      console.error('Error fetching vendor stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorDetails = async (vendorId) => {
    setLoading(true);
    try {
      const [ordersRes, productsRes] = await Promise.all([
        axiosInstance.get(`${API_URL}/api/admin/vendor/${vendorId}/orders`),
        axiosInstance.get(`${API_URL}/api/admin/vendor/${vendorId}/products`)
      ]);
      setVendorOrders(ordersRes.data || []);
      setVendorProducts(productsRes.data || []);
    } catch (error) {
      console.error('Error fetching vendor details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      packed: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const selectedVendorData = vendors.find(v => v.id === selectedVendor || v._id === selectedVendor);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Analytics</h1>
          <p className="text-gray-600">View vendor-wise orders, products, and performance</p>
        </div>
        <Button onClick={fetchAllVendorStats} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Vendor Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <label className="font-medium text-gray-700">Select Vendor:</label>
            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors (Overview)</SelectItem>
                {vendors.map((vendor) => (
                  <SelectItem key={vendor.id || vendor._id} value={vendor.id || vendor._id}>
                    {vendor.business_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Overview - All Vendors */}
      {selectedVendor === 'all' && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">All Vendors Summary</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Store className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Vendors</p>
                    <p className="text-2xl font-bold">{vendors.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <IndianRupee className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold">{formatPrice(vendorStats.totalRevenue || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <ShoppingCart className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Orders</p>
                    <p className="text-2xl font-bold">{vendorStats.totalOrders || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Package className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Products</p>
                    <p className="text-2xl font-bold">{vendorStats.totalProducts || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Vendor-wise Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle>Vendor-wise Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">Vendor</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-right p-3 font-medium">Products</th>
                      <th className="text-right p-3 font-medium">Orders</th>
                      <th className="text-right p-3 font-medium">Revenue</th>
                      <th className="text-center p-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(vendorStats.vendorBreakdown || []).map((vendor, index) => (
                      <tr key={vendor.vendor_id || index} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div className="font-medium">{vendor.business_name}</div>
                          <div className="text-sm text-gray-500">{vendor.email}</div>
                        </td>
                        <td className="p-3">
                          <Badge className={vendor.approval_status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {vendor.approval_status}
                          </Badge>
                        </td>
                        <td className="p-3 text-right font-medium">{vendor.product_count || 0}</td>
                        <td className="p-3 text-right font-medium">{vendor.order_count || 0}</td>
                        <td className="p-3 text-right font-medium text-green-600">
                          {formatPrice(vendor.total_revenue || 0)}
                        </td>
                        <td className="p-3 text-center">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedVendor(vendor.vendor_id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Single Vendor View */}
      {selectedVendor !== 'all' && selectedVendorData && (
        <div className="space-y-6">
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {selectedVendorData.business_name?.[0] || 'V'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedVendorData.business_name}</h2>
                    <p className="text-gray-600">{selectedVendorData.email}</p>
                    <Badge className={selectedVendorData.approval_status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {selectedVendorData.approval_status}
                    </Badge>
                  </div>
                </div>
                <Button variant="outline" onClick={() => setSelectedVendor('all')}>
                  Back to All Vendors
                </Button>
              </div>
            </CardContent>
          </Card>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="orders">Orders ({vendorOrders.length})</TabsTrigger>
              <TabsTrigger value="products">Products / SKU ({vendorProducts.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="orders" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Vendor Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  {vendorOrders.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No orders found for this vendor</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-medium">Order ID</th>
                            <th className="text-left p-3 font-medium">Customer</th>
                            <th className="text-left p-3 font-medium">Date</th>
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-left p-3 font-medium">Payment</th>
                            <th className="text-right p-3 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vendorOrders.map((order) => (
                            <tr key={order.id || order._id} className="border-b hover:bg-gray-50">
                              <td className="p-3 font-mono text-sm">
                                #{(order.id || order._id)?.slice(-8).toUpperCase()}
                              </td>
                              <td className="p-3">
                                <div className="font-medium">
                                  {order.customer_name || order.guest_name || 'Guest'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {order.customer_phone || order.guest_phone || order.shipping_address?.phone || '-'}
                                </div>
                              </td>
                              <td className="p-3 text-sm text-gray-600">
                                {new Date(order.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </td>
                              <td className="p-3">
                                <Badge className={getStatusColor(order.order_status)}>
                                  {order.order_status}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                  {order.payment_method || 'COD'}
                                </Badge>
                              </td>
                              <td className="p-3 text-right font-medium text-green-600">
                                {formatPrice(order.total || order.total_amount || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="products" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Vendor Products / SKU</CardTitle>
                </CardHeader>
                <CardContent>
                  {vendorProducts.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No products found for this vendor</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-medium">Product</th>
                            <th className="text-left p-3 font-medium">SKU</th>
                            <th className="text-left p-3 font-medium">Category</th>
                            <th className="text-right p-3 font-medium">Price</th>
                            <th className="text-right p-3 font-medium">Stock</th>
                            <th className="text-left p-3 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vendorProducts.map((product) => (
                            <tr key={product.id || product._id} className="border-b hover:bg-gray-50">
                              <td className="p-3">
                                <div className="flex items-center gap-3">
                                  {product.images?.[0] && (
                                    <img 
                                      src={product.images[0]} 
                                      alt={product.name}
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                  )}
                                  <span className="font-medium">{product.name}</span>
                                </div>
                              </td>
                              <td className="p-3 font-mono text-sm text-gray-600">
                                {product.sku || (product.id || product._id)?.slice(-8).toUpperCase()}
                              </td>
                              <td className="p-3 text-gray-600">{product.category || '-'}</td>
                              <td className="p-3 text-right font-medium">{formatPrice(product.price)}</td>
                              <td className="p-3 text-right">
                                <span className={product.stock < 10 ? 'text-red-600 font-medium' : ''}>
                                  {product.stock}
                                </span>
                              </td>
                              <td className="p-3">
                                <Badge className={product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                  {product.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
